import { CdkTree, CdkTreeModule } from '@angular/cdk/tree';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { concatMap, debounceTime, defer, distinctUntilChanged, finalize, from, map, Observable, of, tap } from 'rxjs';

import { FileItemModel } from '@models/module/fields/main';
import {
  ExplorerBreadcrumbItem,
  ExplorerNode,
} from '@module-definition/file-explorer/common/models/file-explorer.models';
import {
  getExplorerItemEntityInstanceId,
  getExplorerItemKey,
  isExplorerEntityInstance,
} from '@module-definition/file-explorer/common/utils/explorer-identity.utils';
import { ModuleIconComponent } from '@shared/components/module-icon/module-icon.component';
import { DEBOUNCE_TIMES, FOLDER_IDS } from '@shared/consts';
import { ContextStore } from '@signal-store/context.store';
import { DictionaryStore } from '@signal-store/dictionary.store';
import { ContextModule } from '@system/context/context.model';

type TreeNode = ExplorerNode & { badge?: string; level?: number };

const SYSTEM_FOLDER_ICONS: Record<number, string> = {
  [FOLDER_IDS.RECYCLEBIN]: 'delete',
  [FOLDER_IDS.SHAREDWITHME]: 'person',
  [FOLDER_IDS.ARCHIVE]: 'archive',
};

/** Badge icon shown over a folder: its system icon, or the globe for global folders. */
function computeBadge(node: TreeNode): string {
  return SYSTEM_FOLDER_ICONS[node.id] ?? (node.global > 1 ? 'public' : null);
}

/** Mirrors legacy `compare`: system folders, regular folders, then hierarchy entities, each by name. */
function compareNodes(a: TreeNode, b: TreeNode): number {
  const aIsSystem = a.id in SYSTEM_FOLDER_ICONS;
  const bIsSystem = b.id in SYSTEM_FOLDER_ICONS;
  if (aIsSystem !== bIsSystem) {
    return aIsSystem ? -1 : 1;
  }

  // A Program root can contain both user folders and Project entity instances. Keep the folders
  // together so hierarchy entities do not get mixed into the alphabetical folder listing.
  const aIsEntityInstance = isExplorerEntityInstance(a);
  const bIsEntityInstance = isExplorerEntityInstance(b);
  if (aIsEntityInstance !== bIsEntityInstance) {
    return aIsEntityInstance ? 1 : -1;
  }

  return (a.name || '').localeCompare(b.name || '', undefined, { numeric: true });
}

@Component({
  selector: 'cipo-folder-tree',
  templateUrl: 'folder-tree.component.html',
  styleUrls: ['folder-tree.component.scss'],
  imports: [
    CdkTreeModule,
    MatButtonModule,
    MatIconModule,
    FlexLayoutModule,
    NgxSkeletonLoaderModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    ModuleIconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FolderTreeComponent implements OnInit {
  private dictionaryStore = inject(DictionaryStore);
  private contextStore = inject(ContextStore);

  /** entity instance ID of the contract. Required only for _api/ProjectFolderFile requests */
  entityInstanceId = input<number>(undefined);
  /** Module of the root context entity (Contract/Project/Program). Project Files requests only. */
  rootModuleId = input<number>();
  /** Whether to show the search input. This performs local search, not a new request.
   * @default false
   */
  includeSearch = input(false);
  /** Optional externally rendered search field, used when the tree viewport must scroll independently. */
  searchTerm = input<string>();
  /** Whether to include the folders with negative ids
   * @default false
   */
  includeSystemFolders = input(false);
  /** Synthetic root nodes to prepend that the backend does not return (e.g. the "Data Exports" node). */
  extraRootNodes = input<FileItemModel[]>([]);
  /**
   * Lists global folders instead of the current context's tree (Project Files only). Needed when
   * picking a destination for a global folder, which cannot be moved into a per-contract one.
   */
  isGlobal = input(false);
  /** Whether the empty state offers a shortcut to create the first folder.
   * @default false
   */
  canAddFolder = input(false);
  /** emits the clicked node, or null (if unselected) */
  nodeClick = output<TreeNode>();
  /** Emitted from the empty state's "Add New Folder" shortcut (legacy explorer1.2.html:56). */
  addFolder = output<void>();

  /**
   * Controlled mode (optional): the folder to select/expand/scroll to from outside (content,
   * breadcrumb). When set, the tree syncs to it without emitting `nodeClick`. Consumers that don't
   * pass it keep the uncontrolled behavior.
   */
  activeNodeKey = input<string>();
  /** Ancestor occurrence keys (root → current) used to expand the path to `activeNodeKey`. */
  activePath = input<string[]>([]);

  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private tree = viewChild<CdkTree<TreeNode>>('tree');

  private nodesMap = new Map<string, TreeNode>();
  /** Folders from the content-area response that arrived before their parent node was in the tree. */
  private pendingChildren = new Map<string, FileItemModel[]>();
  selectedNode = signal<TreeNode>(null);
  private datasource = signal<TreeNode[]>(null);
  private syncingTo: string = null;
  private contextKey: string = null;

  private readonly loadingNodeKeys = signal<ReadonlySet<string>>(new Set());
  protected readonly isLoadingChildren = (key: string) => this.loadingNodeKeys().has(key);

  /** Static skeleton rows that mimic the real tree structure (indent + icon + text). */
  readonly skeletonRows: { indent: number; hasChevron: boolean; width: string }[] = [
    { indent: 0, hasChevron: true, width: '70%' },
    { indent: 0, hasChevron: true, width: '55%' },
    { indent: 40, hasChevron: false, width: '60%' },
    { indent: 40, hasChevron: false, width: '45%' },
    { indent: 40, hasChevron: false, width: '75%' },
    { indent: 0, hasChevron: true, width: '50%' },
    { indent: 0, hasChevron: false, width: '65%' },
    { indent: 0, hasChevron: true, width: '80%' },
    { indent: 40, hasChevron: false, width: '55%' },
    { indent: 0, hasChevron: false, width: '40%' },
  ];

  searchControl = new FormControl<string>('');
  private readonly internalSearchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(DEBOUNCE_TIMES.short), distinctUntilChanged()),
  );
  private readonly effectiveSearchValue = computed(() => this.searchTerm() ?? this.internalSearchValue());
  filteredDatasource = computed(() => {
    const datasource = this.datasource();
    const criteria = this.effectiveSearchValue()?.toLowerCase().trim();
    if (!criteria) {
      return datasource;
    }
    return this.filterTree(datasource, criteria);
  });

  protected readonly isSearching = computed(() => !!this.effectiveSearchValue()?.trim());

  constructor() {
    // Controlled sync: follow the contextual occurrence key from outside without emitting back.
    effect(() => {
      const targetKey = this.activeNodeKey();
      const path = this.activePath();
      this.datasource(); // re-run once roots/children are loaded
      untracked(() => {
        if (targetKey == null || this.selectedNode()?.explorerKey === targetKey || this.syncingTo === targetKey) {
          return;
        }
        this.syncToActive(targetKey, path);
      });
    });

    // The roots belong to one entity instance, so they have to be refetched when the host switches
    // to another one. The first run only records the bound values — `ngOnInit` already loaded those.
    effect(() => {
      const contextKey = `${this.entityInstanceId()}:${this.rootModuleId()}`;
      untracked(() => {
        if (this.contextKey === null || contextKey === this.contextKey) {
          this.contextKey = contextKey;
          return;
        }
        this.contextKey = contextKey;
        this.reload();
      });
    });
  }

  ngOnInit() {
    this.loadRoots();
  }

  trackBy = (index: number, node: TreeNode) => node.explorerKey;
  expansionKey = (node: TreeNode) => node.explorerKey;

  getChildrenAccessor = (node: TreeNode) => node.children ?? [];

  /** The tree receives entity instances without their icon metadata, unlike the data list. */
  entityModule(node: TreeNode): ContextModule {
    return Object.values(this.contextStore.modules()).find(module => module.moduleId === node.itemModuleId);
  }

  onExpand(node: TreeNode, expanded: boolean) {
    const target = this.nodesMap.get(node.explorerKey) ?? node;
    if (expanded && target.hasChildren !== false) {
      this.loadChildren(target, { reload: true }).subscribe();
    }
  }

  selectNode(node: TreeNode) {
    const next = this.selectedNode()?.explorerKey === node.explorerKey ? null : node;
    this.selectedNode.set(next);
    this.nodeClick.emit(next);
    if (!next) {
      return;
    }

    const target = this.nodesMap.get(node.explorerKey) ?? node;
    if (target.hasChildren) {
      this.tree()?.expand(target);
    }
  }

  clearSelection() {
    this.selectedNode.set(null);
  }

  /**
   * Feeds the folders from the content-area response into the tree node's children.
   * Mirrors legacy `processListFolders`: the two searches disagree often enough — the folders-only one
   * sometimes comes back short — that the tree takes the union of both. Adding only, never replacing,
   * also means paginating or sorting the content can bring more folders in but never wipe any.
   */
  updateNodeChildren(folderKey: string | number, items: FileItemModel[]) {
    const folders = items.filter(item => item.isFolder && (this.includeSystemFolders() || item.id >= 0));
    const resolvedKey = this.resolveNodeKey(folderKey);
    const node = resolvedKey ? this.nodesMap.get(resolvedKey) : null;
    if (!node) {
      // The tree hasn't expanded to this node yet — buffer the folders so they can be merged once
      // the node loads. This handles the race where the content response arrives before the
      // folder-only tree search finishes expanding.
      if (folders.length) {
        const pendingKey = resolvedKey ?? `${folderKey}`;
        this.pendingChildren.set(pendingKey, folders);
      }
      return;
    }
    if (!folders.length) {
      // A page of content without folders says nothing about the node: only the folders-only search,
      // which reports `hasChildren`, may conclude that there are none.
      if (node.hasChildren === false && !node.children) {
        node.children = [];
        this.datasource.update(ds => [...ds]);
      }
      return;
    }
    this.mergeChildren(node, folders);
    this.tree()?.expand(node);
  }

  /**
   * Returns the breadcrumb ancestor chain [{id, name}] for a node, from root down to
   * (but not including) the node itself.
   *
   * Prefers where the node actually sits in the rendered tree, because `parents` describes the
   * folder's own lineage, which is not always where it is shown: a deleted folder is listed under
   * Recycle Bin but still reports its original parents, so trusting `parents` would drop the bin
   * out of the breadcrumb. Falls back to `parents` for nodes that are not rendered (yet).
   */
  getAncestorPath(node: FileItemModel | ExplorerNode): ExplorerBreadcrumbItem[] {
    const nodeKey = getExplorerItemKey(node as ExplorerNode, this.entityInstanceId());
    const rendered = this.findRenderedPath(this.datasource() ?? [], nodeKey);
    if (rendered) {
      return rendered.slice(0, -1).map(n => this.toBreadcrumbItem(n));
    }

    return (node.parents ?? [])
      .map(p => {
        if (this.isRuntimeParentModel(p)) {
          return { id: p.id, isEntityInstance: p.isEntityInstance };
        }
        return { id: p as number, isEntityInstance: false };
      })
      .map(p => {
        const ancestor = [...this.nodesMap.values()].find(
          candidate => candidate.id === p.id && isExplorerEntityInstance(candidate) === p.isEntityInstance,
        );
        return ancestor ? this.toBreadcrumbItem(ancestor) : null;
      })
      .filter((p): p is ExplorerBreadcrumbItem => p !== null);
  }

  /** Walks the loaded tree for one occurrence key, returning the chain of nodes from a root down to it. */
  private findRenderedPath(nodes: TreeNode[], key: string): TreeNode[] {
    for (const node of nodes) {
      if (node.explorerKey === key) {
        return [node];
      }
      const found = node.children?.length ? this.findRenderedPath(node.children, key) : null;
      if (found) {
        return [node, ...found];
      }
    }
    return null;
  }

  private syncToActive(targetKey: string, path: string[]) {
    this.syncingTo = targetKey;
    // Expand the whole chain including the target, so the opened folder shows its children.
    from(path || [])
      .pipe(concatMap(key => this.ensureExpanded(key)))
      .subscribe({
        complete: () => {
          const node = this.nodesMap.get(targetKey);
          if (node) {
            this.selectedNode.set(node);
            this.scrollToNode(targetKey);
          } else {
            this.selectedNode.set(null);
          }
          this.syncingTo = null;
        },
      });
  }

  private ensureExpanded(key: string): Observable<void> {
    const node = this.nodesMap.get(key);
    if (!node) {
      return of(undefined);
    }
    const load$ = node.children || node.hasChildren === false ? of(undefined) : this.loadChildren(node);
    return load$.pipe(tap(() => this.tree()?.expand(node)));
  }

  private scrollToNode(key: string) {
    setTimeout(() => {
      this.host.nativeElement
        .querySelector(`[data-node-key="${CSS.escape(key)}"]`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });
  }

  reload() {
    this.tree()?.collapseAll();
    this.dictionaryStore.clearFolderFiles();
    this.nodesMap.clear();
    this.pendingChildren.clear();
    this.selectedNode.set(null);
    this.syncingTo = null;
    this.loadRoots();
  }

  refreshNode(folderKey: string | number) {
    const resolvedKey = this.resolveNodeKey(folderKey);
    const node = resolvedKey ? this.nodesMap.get(resolvedKey) : null;
    if (!node) {
      this.loadRoots(true, true);
      return;
    }

    this.loadChildren(node, { reload: true, replace: true }).subscribe();
  }

  loadRoots(keepCurrent = false, forceReload = false) {
    if (!keepCurrent) {
      this.datasource.set(null);
    }
    const entityInstanceId = this.entityInstanceId();
    const contextKey = `${entityInstanceId}:${this.rootModuleId()}:${this.isGlobal()}`;
    this.dictionaryStore
      /* -1 is the root folder */
      .getFolderFiles(entityInstanceId || -1, entityInstanceId, true, this.rootModuleId(), this.isGlobal(), forceReload)
      .pipe(map(data => (this.includeSystemFolders() ? data : data.filter(({ id }) => id >= 0))))
      .subscribe(data => {
        // Context changes can issue a new root request before the previous one finishes. Ignore a
        // late response for the old entity so it cannot replace the newly selected tree.
        if (contextKey !== `${this.entityInstanceId()}:${this.rootModuleId()}:${this.isGlobal()}`) {
          return;
        }
        const extras = this.extraRootNodes().map(node => this.decorateNode(node));
        const roots = [
          ...extras,
          ...data
            .map(node => this.decorateNode(node))
            .filter(node => !extras.some(extra => extra.explorerKey === node.explorerKey)),
        ];
        roots.forEach((node: TreeNode) => {
          const known = this.nodesMap.get(node.explorerKey);
          if (known?.children) {
            node.children = known.children;
          }
          node.level = 0;
          node.badge = computeBadge(node);
          this.nodesMap.set(node.explorerKey, node);
        });
        this.datasource.set([...roots].sort(compareNodes));
        // Apply any content-area folders that arrived before these roots were in the map.
        roots.forEach((node: TreeNode) => this.drainPending(node));
      });
  }

  /**
   * @param reload skips the cached folder list and asks the server again
   * @param replace drops the folders the node already has instead of merging, so that a node refreshed
   * after a delete or a move loses what is really gone
   */
  private loadChildren(node: TreeNode, { reload = false, replace = false } = {}): Observable<void> {
    // `defer` so the loading flag is tied to an actual subscription, not to building the stream.
    return defer(() => {
      this.loadingNodeKeys.update(keys => new Set(keys).add(node.explorerKey));
      const entityInstanceId = getExplorerItemEntityInstanceId(node, this.entityInstanceId());
      return this.dictionaryStore.getFolderFiles(
        isExplorerEntityInstance(node) ? FOLDER_IDS.ROOT : node.id,
        entityInstanceId,
        true,
        this.rootModuleId(),
        this.isGlobal(),
        reload,
      );
    }).pipe(
      finalize(() =>
        this.loadingNodeKeys.update(keys => {
          const next = new Set(keys);
          next.delete(node.explorerKey);
          return next;
        }),
      ),
      map(data => (this.includeSystemFolders() ? data : data.filter(({ id }) => id >= 0))),
      tap((data: TreeNode[]) => this.mergeChildren(node, data, replace)),
      map(() => undefined),
    );
  }

  /**
   * Adds the given folders to a node, keeping the ones it already has unless asked to replace them.
   * The folders-only search and the content search do not always agree on what lives under a folder,
   * so the tree shows the union of the two rather than letting whichever answers last win.
   */
  private mergeChildren(node: TreeNode, items: FileItemModel[], replace = false) {
    const merged: TreeNode[] = replace ? [] : (node.children ?? []).slice();
    const byKey = new Map(merged.map(child => [child.explorerKey, child]));
    for (const item of items) {
      const child = this.decorateNode(item, node);
      const known = byKey.get(child.explorerKey);
      if (known) {
        Object.assign(known, item);
        known.badge = computeBadge(known);
        continue;
      }
      const previous = this.nodesMap.get(child.explorerKey);
      if (previous?.children) {
        child.children = previous.children;
      }
      child.level = (node.level || 0) + 1;
      child.badge = computeBadge(child);
      merged.push(child);
      byKey.set(child.explorerKey, child);
      this.nodesMap.set(child.explorerKey, child);
    }
    node.children = merged.sort(compareNodes);
    node.hasChildren = node.children.length > 0;
    this.datasource.update(ds => [...ds]);
    // Newly registered children may have pending content-area folders waiting for them.
    for (const child of node.children) {
      this.drainPending(child);
    }
  }

  /**
   * If the content-area response arrived before this node existed in the tree, the folders were
   * buffered in `pendingChildren`. Now that the node is registered, merge them in.
   */
  private drainPending(node: TreeNode) {
    const pending = this.pendingChildren.get(node.explorerKey) ?? this.pendingChildren.get(`${node.id}`);
    if (!pending) {
      return;
    }
    this.pendingChildren.delete(node.explorerKey);
    this.pendingChildren.delete(`${node.id}`);
    this.mergeChildren(node, pending);
    this.tree()?.expand(node);
  }

  /** Keeps the nodes matching `criteria` plus the ancestors needed to reach them. */
  private filterTree(nodes: TreeNode[], criteria: string): TreeNode[] {
    const result: TreeNode[] = [];
    if (!nodes?.length || !criteria) {
      return result;
    }

    for (const node of nodes) {
      if (node.name?.toLowerCase().includes(criteria)) {
        result.push(node);
        continue;
      }

      const filteredChildren = node.children ? this.filterTree(node.children, criteria) : [];
      if (filteredChildren.length) {
        result.push({ ...node, children: filteredChildren });
      }
    }

    return result;
  }

  /**
   * Type guard to safely validate the backend payload at runtime.
   * Ensures the item is an object containing at least an 'id' property.
   */
  private isRuntimeParentModel(item: unknown): item is { id: number; isEntityInstance: boolean } {
    return typeof item === 'object' && item !== null && 'id' in item;
  }

  private decorateNode(item: FileItemModel, parent?: TreeNode): TreeNode {
    const node = item as TreeNode;
    node.isEntityInstance = isExplorerEntityInstance(node);
    node.parentExplorerKey ??= parent?.explorerKey;
    node.parentEntityInstanceId ??= getExplorerItemEntityInstanceId(parent, this.entityInstanceId());
    node.explorerKey = getExplorerItemKey(node, node.parentEntityInstanceId ?? this.entityInstanceId());
    return node;
  }

  private resolveNodeKey(keyOrId: string | number): string {
    if (typeof keyOrId === 'string' && this.nodesMap.has(keyOrId)) {
      return keyOrId;
    }
    return [...this.nodesMap.values()].find(node => node.id === Number(keyOrId))?.explorerKey;
  }

  private toBreadcrumbItem(node: TreeNode): ExplorerBreadcrumbItem {
    return {
      id: node.id,
      name: node.name,
      explorerKey: node.explorerKey,
      parentExplorerKey: node.parentExplorerKey,
      parentEntityInstanceId: getExplorerItemEntityInstanceId(node, this.entityInstanceId()),
      parentsEIString: node.parentsEIString,
      isEntityInstance: node.isEntityInstance,
      itemModuleId: node.itemModuleId,
      destinationEntityInstanceId: node.destinationEntityInstanceId,
    };
  }
}
